import { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";
import { getErrorMessage } from "@/lib/errorHandler";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Search, Users, Download, ListOrdered, CheckCircle2, PlayCircle, Circle,
  FileText, Layers,
} from "lucide-react";

const PAGE_SIZE = 20;

export default function MyQuizTakers() {
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { selectedCampus, isOrgAdmin, isMultiCampusAdmin } = useCampusContext();

  const [mode, setMode] = useState("sequence"); // "sequence" | "direct"

  // Sequence-mode state
  const [sequences, setSequences] = useState([]);
  const [sequencesLoading, setSequencesLoading] = useState(true);
  const [sequenceId, setSequenceId] = useState("");
  const [seqMeta, setSeqMeta] = useState(null);

  // Direct-mode state
  const [quizzes, setQuizzes] = useState([]);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [quizId, setQuizId] = useState("");
  const [quizMeta, setQuizMeta] = useState(null);

  // Shared table state
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [progressFilter, setProgressFilter] = useState("all");
  const [exporting, setExporting] = useState(false);

  // Common tenancy params — every fetch on this page uses the same.
  const tenantParams = useMemo(() => {
    const p = {};
    if (isSuperadmin && selectedOrg?.id) p.organization_id = selectedOrg.id;
    if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) p.campus_id = selectedCampus.id;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin, selectedOrg?.id, isOrgAdmin, isMultiCampusAdmin, selectedCampus?.id]);

  const isSuperadminWithoutOrg = isSuperadmin && !selectedOrg;

  // Load sequences on mount / tenancy change (only when in sequence mode).
  useEffect(() => {
    if (mode !== "sequence") return;
    if (isSuperadminWithoutOrg) {
      setSequences([]); setSequenceId(""); setSequencesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setSequencesLoading(true);
      try {
        const res = await API.get("/user-management/sequence-takers/sequences", { params: tenantParams });
        if (cancelled) return;
        const list = res.data?.sequences || [];
        setSequences(list);
        if (list.length === 1) setSequenceId(list[0].id);
        else if (!list.find((s) => s.id === sequenceId)) setSequenceId("");
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, "Failed to load sequences"));
      } finally {
        if (!cancelled) setSequencesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tenantParams, isSuperadminWithoutOrg]);

  // Load quizzes on mount / tenancy change (only when in direct mode).
  useEffect(() => {
    if (mode !== "direct") return;
    if (isSuperadminWithoutOrg) {
      setQuizzes([]); setQuizId(""); setQuizzesLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setQuizzesLoading(true);
      try {
        const res = await API.get("/user-management/direct-takers/quizzes", { params: tenantParams });
        if (cancelled) return;
        const list = res.data?.quizzes || [];
        setQuizzes(list);
        if (list.length === 1) setQuizId(list[0].id);
        else if (!list.find((q) => q.id === quizId)) setQuizId("");
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, "Failed to load quizzes"));
      } finally {
        if (!cancelled) setQuizzesLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tenantParams, isSuperadminWithoutOrg]);

  // Debounce search.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Reset paging + search when mode or selection changes.
  useEffect(() => { setPage(1); }, [mode, sequenceId, quizId, debouncedSearch, progressFilter]);
  // Also clear data on mode change — the two modes have different row shapes,
  // so a lingering render before the next fetch responds would blow up the table.
  useEffect(() => {
    setSearch(""); setDebouncedSearch(""); setProgressFilter("all");
    setData([]); setSeqMeta(null); setQuizMeta(null); setTotal(0); setSummary(null);
  }, [mode]);
  // Different entity → the old filter may not make sense; keep it simple.
  useEffect(() => { setProgressFilter("all"); }, [sequenceId, quizId]);

  // Load takers whenever the selected entity changes.
  useEffect(() => {
    const selectedId = mode === "sequence" ? sequenceId : quizId;
    if (!selectedId) {
      setData([]); setSeqMeta(null); setQuizMeta(null); setTotal(0);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { ...tenantParams, page, limit: PAGE_SIZE };
        if (debouncedSearch) params.search = debouncedSearch;
        const endpoint =
          mode === "sequence" ? "/user-management/sequence-takers" : "/user-management/direct-takers";
        if (mode === "sequence") params.sequence_id = sequenceId;
        else params.quiz_id = quizId;
        if (progressFilter !== "all") {
          if (mode === "sequence") params.progress = progressFilter;
          else params.status = progressFilter;
        }

        const res = await API.get(endpoint, { params });
        if (cancelled) return;
        setData(res.data?.data || []);
        setSeqMeta(mode === "sequence" ? res.data?.sequence || null : null);
        setQuizMeta(mode === "direct" ? res.data?.quiz || null : null);
        setTotal(res.data?.total || 0);
        setSummary(res.data?.summary || null);
      } catch (err) {
        if (!cancelled) toast.error(getErrorMessage(err, "Failed to load participants"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [mode, sequenceId, quizId, page, debouncedSearch, progressFilter, tenantParams]);

  const exportCSV = async () => {
    const selectedId = mode === "sequence" ? sequenceId : quizId;
    if (!selectedId) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (mode === "sequence") params.append("sequence_id", sequenceId);
      else params.append("quiz_id", quizId);
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (progressFilter !== "all") {
        params.append(mode === "sequence" ? "progress" : "status", progressFilter);
      }
      if (tenantParams.organization_id) params.append("organization_id", tenantParams.organization_id);

      const endpoint =
        mode === "sequence"
          ? "/user-management/sequence-takers/export/csv"
          : "/user-management/direct-takers/export/csv";
      const res = await API.get(`${endpoint}?${params.toString()}`, { responseType: "blob" });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      const filenamePrefix = mode === "sequence" ? "sequence-takers" : "direct-takers";
      const slug = (mode === "sequence" ? seqMeta?.slug : quizMeta?.slug) || selectedId;
      link.download = `${filenamePrefix}-${slug}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to export CSV"));
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (s) => (s ? new Date(s).toLocaleString() : "—");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const selectorReady = mode === "sequence" ? !!sequenceId : !!quizId;

  return (
    <>
      <style>{`

        .qt-root { font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif; }
        .qt-heading { font-family: 'Lexend', sans-serif; font-weight: 600; letter-spacing: -0.025em; }
        .qt-eyebrow {
          font-family: 'Lexend', sans-serif;
          font-size: 10.5px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          font-weight: 600;
          color: rgb(100 116 139);
        }

        .qt-card { background: white; border: 1px solid rgb(226 232 240); border-radius: 12px; }
        .qt-card-hero {
          background: linear-gradient(135deg, rgb(238 242 255) 0%, rgb(250 245 255) 100%);
          border: 1px solid rgb(199 210 254);
          border-radius: 16px;
          position: relative;
          overflow: hidden;
        }
        .qt-card-hero::before {
          content: "";
          position: absolute;
          right: -30px; top: -30px;
          width: 180px; height: 180px;
          background: radial-gradient(circle, rgb(165 180 252 / 0.35) 0%, transparent 60%);
          pointer-events: none;
        }

        /* Mode toggle — segmented control */
        .qt-mode {
          display: inline-flex;
          padding: 3px;
          background: white;
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          gap: 2px;
        }
        .qt-mode-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          padding: 7px 14px;
          border-radius: 8px;
          font-family: 'Lexend', sans-serif;
          font-size: 12.5px;
          font-weight: 500;
          border: none;
          background: transparent;
          color: rgb(100 116 139);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .qt-mode-btn:hover:not(.active) { color: rgb(51 65 85); background: rgb(248 250 252); }
        .qt-mode-btn.active {
          background: rgb(15 23 42);
          color: white;
          box-shadow: 0 1px 2px 0 rgb(15 23 42 / 0.15);
        }

        .qt-stat { background: white; border: 1px solid rgb(226 232 240); border-radius: 12px; padding: 16px 18px; }
        .qt-stat-label {
          font-family: 'Lexend', sans-serif;
          font-size: 10.5px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          font-weight: 600;
          color: rgb(100 116 139);
          margin-bottom: 8px;
        }
        .qt-stat-value {
          font-family: 'Lexend', sans-serif;
          font-size: 26px;
          font-weight: 700;
          letter-spacing: -0.02em;
          line-height: 1;
          color: rgb(15 23 42);
        }

        .qt-search-input {
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding-left: 40px;
          height: 42px;
          transition: all 0.15s ease;
        }
        .qt-search-input:focus {
          border-color: rgb(99 102 241);
          box-shadow: 0 0 0 3px rgb(99 102 241 / 0.12);
          outline: none;
        }

        .qt-table-card { background: white; border: 1px solid rgb(226 232 240); border-radius: 12px; overflow: hidden; }
        .qt-table { width: 100%; border-collapse: collapse; }
        .qt-table thead tr {
          background: linear-gradient(to bottom, rgb(248 250 252), rgb(241 245 249));
          border-bottom: 1px solid rgb(226 232 240);
        }
        .qt-table th {
          font-family: 'Lexend', sans-serif;
          font-size: 11.5px;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: rgb(71 85 105);
          padding: 12px 16px;
          text-align: left;
        }
        .qt-table tbody tr {
          border-bottom: 1px solid rgb(241 245 249);
          transition: background 0.12s ease;
        }
        .qt-table tbody tr:hover { background: rgb(248 250 252); }
        .qt-table td { padding: 14px 16px; font-size: 13.5px; color: rgb(51 65 85); vertical-align: middle; }

        .qt-chip-strip { display: flex; flex-wrap: wrap; gap: 6px; max-width: 460px; }
        .qt-chip {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 9px;
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 500;
          border: 1px solid;
          font-family: 'DM Sans', sans-serif;
          max-width: 220px;
        }
        .qt-chip .qt-chip-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 130px; }
        .qt-chip.done { background: rgb(240 253 244); color: rgb(21 128 61); border-color: rgb(187 247 208); }
        .qt-chip.doing { background: rgb(254 252 232); color: rgb(161 98 7); border-color: rgb(253 224 71 / 0.6); }
        .qt-chip.idle { background: rgb(248 250 252); color: rgb(100 116 139); border-color: rgb(226 232 240); }

        .qt-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 3px 10px;
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 600;
          font-family: 'Lexend', sans-serif;
          border: 1px solid;
        }
        .qt-status-pill.done { background: rgb(240 253 244); color: rgb(22 101 52); border-color: rgb(187 247 208); }
        .qt-status-pill.doing { background: rgb(254 252 232); color: rgb(161 98 7); border-color: rgb(253 224 71 / 0.6); }
        .qt-status-pill.expired { background: rgb(254 242 242); color: rgb(153 27 27); border-color: rgb(254 202 202); }

        .qt-progress-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 4px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Lexend', sans-serif;
          background: rgb(238 242 255);
          color: rgb(67 56 202);
          border: 1px solid rgb(199 210 254);
        }
        .qt-progress-pill.full { background: rgb(240 253 244); color: rgb(22 101 52); border-color: rgb(187 247 208); }

        .qt-score {
          font-family: 'Lexend', sans-serif;
          font-weight: 600;
          color: rgb(15 23 42);
          font-size: 13.5px;
        }
        .qt-score-max { color: rgb(100 116 139); font-weight: 500; }

        .qt-empty { padding: 72px 24px; text-align: center; background: linear-gradient(to bottom, white, rgb(248 250 252)); }

        .qt-pagination {
          background: linear-gradient(to right, rgb(248 250 252), white, rgb(248 250 252));
          border: 1px solid rgb(226 232 240);
          border-radius: 10px;
          padding: 12px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }

        @keyframes qtFade {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .qt-fade { animation: qtFade 0.35s ease both; }
        .qt-fade-d { animation: qtFade 0.35s ease 0.06s both; }
      `}</style>

      <div className="qt-root space-y-6 p-6">
        {/* Header */}
        <div className="qt-fade flex items-start justify-between gap-4">
          <div>
            <h1 className="qt-heading text-2xl text-slate-900 flex items-center gap-2.5">
              <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
                <Users className="h-5 w-5 text-violet-700" />
              </div>
              My Quiz Takers
            </h1>
            <p className="text-sm text-slate-500 mt-2 ml-12">
              {mode === "sequence"
                ? "Participants registered on a sequence, with per-assessment status."
                : "Participants who took a specific quiz directly (not through a sequence)."}
            </p>
          </div>
          <div className="pt-1">
            <Button
              variant="outline"
              onClick={exportCSV}
              disabled={!selectorReady || exporting}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 gap-1.5"
              title="Export the currently displayed roster"
            >
              <Download className="h-4 w-4" />
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Hero: mode toggle + selector + stats */}
        <div className="qt-card-hero qt-fade-d p-5 md:p-6">
          <div className="relative space-y-4">
            {/* Mode toggle */}
            <div className="qt-mode">
              <button
                className={`qt-mode-btn ${mode === "sequence" ? "active" : ""}`}
                onClick={() => setMode("sequence")}
                data-testid="mode-sequence"
              >
                <Layers className="w-3.5 h-3.5" /> By sequence
              </button>
              <button
                className={`qt-mode-btn ${mode === "direct" ? "active" : ""}`}
                onClick={() => setMode("direct")}
                data-testid="mode-direct"
              >
                <FileText className="w-3.5 h-3.5" /> Direct quiz
              </button>
            </div>

            {/* Selector + stats row */}
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1 min-w-0">
                <p className="qt-eyebrow mb-1.5">{mode === "sequence" ? "Sequence" : "Quiz"}</p>
                {mode === "sequence" ? (
                  sequencesLoading ? (
                    <div className="h-11 w-full max-w-sm rounded-md bg-white/60 animate-pulse" />
                  ) : isSuperadminWithoutOrg ? (
                    <p className="text-sm text-slate-600">Pick an organization from the header to see its sequences.</p>
                  ) : sequences.length === 0 ? (
                    <p className="text-sm text-slate-600">No sequences are available for this campus yet.</p>
                  ) : (
                    <Select value={sequenceId} onValueChange={setSequenceId}>
                      <SelectTrigger className="max-w-md h-11 bg-white">
                        <SelectValue placeholder="Choose a sequence…" />
                      </SelectTrigger>
                      <SelectContent>
                        {sequences.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            <div className="flex items-center gap-2">
                              <ListOrdered className="w-3.5 h-3.5 text-indigo-600" />
                              <span className="font-medium">{s.title}</span>
                              <span className="text-xs text-slate-500">
                                · {s.quiz_count} quiz{s.quiz_count === 1 ? "" : "zes"}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : (
                  quizzesLoading ? (
                    <div className="h-11 w-full max-w-sm rounded-md bg-white/60 animate-pulse" />
                  ) : isSuperadminWithoutOrg ? (
                    <p className="text-sm text-slate-600">Pick an organization from the header to see its quizzes.</p>
                  ) : quizzes.length === 0 ? (
                    <p className="text-sm text-slate-600">No direct quiz takers yet on this campus.</p>
                  ) : (
                    <Select value={quizId} onValueChange={setQuizId}>
                      <SelectTrigger className="max-w-md h-11 bg-white">
                        <SelectValue placeholder="Choose a quiz…" />
                      </SelectTrigger>
                      <SelectContent>
                        {quizzes.map((q) => (
                          <SelectItem key={q.id} value={q.id}>
                            <div className="flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5 text-indigo-600" />
                              <span className="font-medium">{q.title}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                )}
              </div>

              {selectorReady && (
                <div className="flex gap-3 flex-wrap md:justify-end">
                  <div className="qt-stat">
                    <div className="qt-stat-label">Participants</div>
                    <div className="qt-stat-value">{summary?.total ?? total}</div>
                  </div>
                  {summary && (
                    <>
                      <div className="qt-stat">
                        <div className="qt-stat-label">Completed</div>
                        <div className="qt-stat-value" style={{ color: "rgb(22 101 52)" }}>{summary.completed}</div>
                      </div>
                      <div className="qt-stat">
                        <div className="qt-stat-label">In progress</div>
                        <div className="qt-stat-value" style={{ color: "rgb(161 98 7)" }}>{summary.in_progress}</div>
                      </div>
                      {mode === "sequence" && (
                        <div className="qt-stat">
                          <div className="qt-stat-label">Not started</div>
                          <div className="qt-stat-value" style={{ color: "rgb(100 116 139)" }}>{summary.not_started}</div>
                        </div>
                      )}
                      {mode === "direct" && summary.expired > 0 && (
                        <div className="qt-stat">
                          <div className="qt-stat-label">Expired</div>
                          <div className="qt-stat-value" style={{ color: "rgb(153 27 27)" }}>{summary.expired}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Search + progress filter */}
        {selectorReady && (
          <div className="qt-card p-4 qt-fade-d">
            <div className="flex gap-2 flex-col sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={mode === "sequence" ? "Search participants by email…" : "Search by name or email…"}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="qt-search-input"
                />
              </div>
              <Select value={progressFilter} onValueChange={setProgressFilter}>
                <SelectTrigger className="h-[42px] w-full sm:w-52 bg-white" data-testid="progress-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {mode === "sequence" ? (
                    <>
                      <SelectItem value="all">All participants</SelectItem>
                      <SelectItem value="completed">Fully completed</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="not_started">Not started</SelectItem>
                    </>
                  ) : (
                    <>
                      <SelectItem value="all">All participants</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In progress</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
              {(search || progressFilter !== "all") && (
                <Button variant="outline" onClick={() => { setSearch(""); setProgressFilter("all"); }}>Clear</Button>
              )}
            </div>
          </div>
        )}

        {/* Table */}
        {selectorReady && (
          <div className="qt-table-card qt-fade-d">
            {loading ? (
              <div className="qt-empty">
                <div className="w-9 h-9 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-slate-500 mt-3">Loading participants…</p>
              </div>
            ) : data.length === 0 ? (
              <div className="qt-empty">
                <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-slate-400" />
                </div>
                <p className="text-slate-700 font-semibold">No participants found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {progressFilter !== "all" || debouncedSearch
                    ? "No one matches the current filter — try clearing it."
                    : mode === "sequence"
                    ? "Participants who register on this sequence will show up here."
                    : "Participants who take this quiz directly will show up here."}
                </p>
              </div>
            ) : mode === "sequence" ? (
              <SequenceTable data={data} formatDate={formatDate} />
            ) : (
              <DirectTable data={data} formatDate={formatDate} />
            )}
          </div>
        )}

        {/* Pagination */}
        {selectorReady && total > PAGE_SIZE && (
          <div className="qt-pagination">
            <p className="text-sm text-slate-600">
              Showing <span className="font-medium text-slate-900">{(page - 1) * PAGE_SIZE + 1}</span>
              –<span className="font-medium text-slate-900">{Math.min(page * PAGE_SIZE, total)}</span>
              {" of "}<span className="font-medium text-slate-900">{total}</span>
            </p>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-600 font-medium">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Timeline cell ─────────────────────────────────────────────────────────────
// One column instead of separate Started/Completed dates — attempts almost
// always start and finish the same day, so the time range + duration is the
// informative part.

function durationLabel(ms) {
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "<1m";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
}

function TimelineCell({ startedAt, completedAt, formatDate }) {
  if (!startedAt) return <span className="text-slate-400">—</span>;
  const start = new Date(startedAt);
  const now = new Date();
  const dayOpts = {
    month: "short",
    day: "numeric",
    ...(start.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  };
  const timeOpts = { hour: "numeric", minute: "2-digit" };
  const startDay = start.toLocaleDateString(undefined, dayOpts);
  const startTime = start.toLocaleTimeString(undefined, timeOpts);

  if (!completedAt) {
    return (
      <div title={`Started ${formatDate(startedAt)}`}>
        <div className="text-slate-700">{startDay}</div>
        <div className="text-xs text-slate-400 mt-0.5">started {startTime}</div>
      </div>
    );
  }

  const end = new Date(completedAt);
  const endTime = end.toLocaleTimeString(undefined, timeOpts);
  const sameDay = start.toDateString() === end.toDateString();
  const dur = durationLabel(end - start);
  return (
    <div title={`Started ${formatDate(startedAt)} · Completed ${formatDate(completedAt)}`}>
      <div className="text-slate-700">
        {sameDay ? startDay : `${startDay} → ${end.toLocaleDateString(undefined, dayOpts)}`}
      </div>
      <div className="text-xs text-slate-400 mt-0.5">
        {startTime} → {endTime}
        {dur && <span className="text-slate-500 font-medium"> · {dur}</span>}
      </div>
    </div>
  );
}

// ── Sub-tables ────────────────────────────────────────────────────────────────

function SequenceTable({ data, formatDate }) {
  // Defensive: only render rows that carry the sequence shape.
  const rows = data.filter((r) => Array.isArray(r?.quiz_results));
  return (
    <div className="overflow-x-auto">
      <table className="qt-table">
        <thead>
          <tr>
            <th>Participant</th>
            <th>Email</th>
            <th>Progress</th>
            <th>Assessments</th>
            <th>Timeline</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const full = r.required_completed ?? (r.quizzes_completed === r.quizzes_total && r.quizzes_total > 0);
            return (
              <tr key={r.attempt_id}>
                <td>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{r.student_name}</span>
                    {r.attempt_number > 1 && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                        Attempt {r.attempt_number}
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-slate-600">{r.student_email}</td>
                <td>
                  <span className={`qt-progress-pill ${full ? "full" : ""}`}>
                    {full && <CheckCircle2 className="w-3.5 h-3.5" />}
                    {r.quizzes_completed}/{r.quizzes_total}
                  </span>
                </td>
                <td>
                  <div className="qt-chip-strip">
                    {r.quiz_results.map((q) => {
                      const cls = q.status === "completed" ? "done" : q.status === "in_progress" ? "doing" : "idle";
                      const Icon = q.status === "completed" ? CheckCircle2 : q.status === "in_progress" ? PlayCircle : Circle;
                      const scoreLabel = q.status === "completed"
                        ? (q.percentage != null ? `${q.percentage}%` : (q.max_score ? `${q.score}/${q.max_score}` : ""))
                        : "";
                      return (
                        <span
                          key={q.quiz_id}
                          className={`qt-chip ${cls}`}
                          title={`${q.quiz_title} · ${q.status.replace("_", " ")}${scoreLabel ? " · " + scoreLabel : ""}`}
                        >
                          <Icon className="w-3 h-3 flex-shrink-0" />
                          <span className="qt-chip-title">{q.quiz_title}</span>
                          {scoreLabel && <span className="opacity-80">· {scoreLabel}</span>}
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td className="text-slate-600">
                  <TimelineCell startedAt={r.started_at} completedAt={r.completed_at} formatDate={formatDate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DirectTable({ data, formatDate }) {
  // Defensive: only render rows that carry the direct shape.
  const rows = data.filter((r) => r?.submission_id);
  return (
    <div className="overflow-x-auto">
      <table className="qt-table">
        <thead>
          <tr>
            <th>Participant</th>
            <th>Email</th>
            <th>Status</th>
            <th>Score</th>
            <th>Attempts</th>
            <th>Timeline</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const statusCls =
              r.status === "completed" ? "done" :
              r.status === "in_progress" ? "doing" :
              "expired";
            const StatusIcon = r.status === "completed" ? CheckCircle2 : PlayCircle;
            return (
              <tr key={r.submission_id}>
                <td>
                  <span className="font-medium text-slate-900">{r.student_name}</span>
                </td>
                <td className="text-slate-600">{r.student_email}</td>
                <td>
                  <span className={`qt-status-pill ${statusCls}`}>
                    <StatusIcon className="w-3 h-3" />
                    {r.status.replace("_", " ")}
                  </span>
                </td>
                <td>
                  {r.status === "completed" ? (
                    r.percentage != null ? (
                      <span className="qt-score">
                        {r.percentage}%
                        {r.max_score ? (
                          <span className="qt-score-max"> · {r.score}/{r.max_score}</span>
                        ) : null}
                      </span>
                    ) : r.max_score ? (
                      <span className="qt-score">{r.score}/{r.max_score}</span>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td>
                  <span className="text-slate-700">
                    <span className="font-semibold">{r.attempts_completed}</span>
                    <span className="text-slate-400"> / {r.attempts_total}</span>
                  </span>
                </td>
                <td className="text-slate-600">
                  <TimelineCell startedAt={r.started_at} completedAt={r.completed_at} formatDate={formatDate} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
